/**
 * Single source of truth for icons.
 *
 * Migrou de phosphor-react → HugeIcons (@hugeicons/core-free-icons).
 * Os nomes públicos foram mantidos pra evitar refactor em ~50 componentes;
 * cada export aqui é um wrapper React que aceita a mesma API que phosphor:
 *
 *   <Icon size={20} weight="bold" color={T.ink} />
 *
 * Theme-aware: por padrão `color="currentColor"`, o que faz o SVG herdar a
 * cor do container (Sidebar, botão, etc.) e alternar automaticamente quando
 * o tema muda. `weight="bold"` aumenta o strokeWidth.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import {
    Add01Icon,
    AddCircleIcon,
    Alert02Icon,
    AlertCircleIcon,
    AnalyticsUpIcon,
    ArrowDown01Icon,
    ArrowLeft01Icon,
    ArrowRight01Icon,
    ArrowUp01Icon,
    ArrowUpRight01Icon,
    BoatIcon,
    BookOpen01Icon,
    Calculator01Icon,
    WarehouseIcon,
    BulbIcon,
    Calendar03Icon,
    Camera01Icon,
    Cancel01Icon,
    CancelCircleIcon,
    ChampionIcon,
    ChartDecreaseIcon,
    ChartIncreaseIcon,
    ChartLineData01Icon,
    CheckmarkCircle02Icon,
    ClipboardIcon,
    Clock01Icon,
    CloudUploadIcon,
    Delete02Icon,
    DollarCircleIcon,
    Drag01Icon,
    DragDropVerticalIcon,
    Download01Icon,
    FileExportIcon,
    FileUploadIcon,
    FilterIcon,
    FlashIcon,
    Home01Icon,
    Home03Icon,
    InformationCircleIcon,
    LoginSquare01Icon,
    LogoutSquare01Icon,
    Mail01Icon,
    MoreVerticalIcon,
    PaintBrush02Icon,
    PercentCircleIcon,
    Package01Icon,
    PencilEdit02Icon,
    Refresh01Icon,
    Rocket02Icon,
    Route01Icon,
    Search01Icon,
    Settings01Icon,
    ShieldUserIcon,
    ShippingTruck01Icon,
    ShoppingCart02Icon,
    SquareLock02Icon,
    Store01Icon,
    Tag01Icon,
    TaskDaily01Icon,
    Tick02Icon,
    TruckIcon,
    UserAddIcon,
    UserGroup03Icon,
    UserIcon,
    UserMultipleIcon,
    ViewIcon,
    ViewOffIcon,
    WrenchIcon,
    PauseIcon,
    PrinterIcon,
} from '@hugeicons/core-free-icons';

/**
 * Fábrica de wrapper que mantém a API do phosphor (size/weight/color).
 *  - `weight="bold"`     → strokeWidth=2 (default 1.5)
 *  - `color="currentColor"` → herda do container, garante troca de tema
 */
function makeIcon(iconData) {
    return function Icon({ size = 18, weight, color = 'currentColor', strokeWidth, ...rest }) {
        const sw = strokeWidth ?? (weight === 'bold' || weight === 'fill' ? 2 : 1.5);
        return <HugeiconsIcon icon={iconData} size={size} color={color} strokeWidth={sw} {...rest} />;
    };
}

// Mapeamento phosphor → HugeIcons (alfabético).
export const ArrowClockwise = makeIcon(Refresh01Icon);
export const ArrowDown = makeIcon(ArrowDown01Icon);
export const ArrowLeft = makeIcon(ArrowLeft01Icon);
export const ArrowRight = makeIcon(ArrowRight01Icon);
export const ArrowSquareOut = makeIcon(ArrowUpRight01Icon);
export const ArrowUp = makeIcon(ArrowUp01Icon);
export const ArrowUpRight = makeIcon(ArrowUpRight01Icon);
export const BookOpen = makeIcon(BookOpen01Icon);
export const Calendar = makeIcon(Calendar03Icon);
export const CalendarBlank = makeIcon(Calendar03Icon);
export const Camera = makeIcon(Camera01Icon);
export const CaretDown = makeIcon(ArrowDown01Icon);
export const CaretLeft = makeIcon(ArrowLeft01Icon);
export const CaretRight = makeIcon(ArrowRight01Icon);
export const CaretUp = makeIcon(ArrowUp01Icon);
export const ChartLine = makeIcon(ChartLineData01Icon);
export const ChartLineUp = makeIcon(ChartIncreaseIcon);
export const Check = makeIcon(Tick02Icon);
export const CheckCircle = makeIcon(CheckmarkCircle02Icon);
export const ClipboardText = makeIcon(ClipboardIcon);
export const Clock = makeIcon(Clock01Icon);
export const CloudArrowUp = makeIcon(CloudUploadIcon);
export const CurrencyDollar = makeIcon(DollarCircleIcon);
export const DotsSixVertical = makeIcon(DragDropVerticalIcon);
export const DownloadSimple = makeIcon(Download01Icon);
export const EnvelopeSimple = makeIcon(Mail01Icon);
export const Eye = makeIcon(ViewIcon);
export const EyeSlash = makeIcon(ViewOffIcon);
export const FileArrowUp = makeIcon(FileUploadIcon);
export const Funnel = makeIcon(FilterIcon);
export const Gear = makeIcon(Settings01Icon);
export const House = makeIcon(Home01Icon);
export const Info = makeIcon(InformationCircleIcon);
export const Lightbulb = makeIcon(BulbIcon);
export const Lightning = makeIcon(FlashIcon);
export const LockKey = makeIcon(SquareLock02Icon);
export const MagnifyingGlass = makeIcon(Search01Icon);
export const Package = makeIcon(Package01Icon);
export const Palette = makeIcon(PaintBrush02Icon);
export const Pause = makeIcon(PauseIcon);
export const Printer = makeIcon(PrinterIcon);
export const PencilSimple = makeIcon(PencilEdit02Icon);
export const Plus = makeIcon(Add01Icon);
export const PlusCircle = makeIcon(AddCircleIcon);
export const ShieldCheck = makeIcon(ShieldUserIcon);
export const SignIn = makeIcon(LoginSquare01Icon);
export const SignOut = makeIcon(LogoutSquare01Icon);
export const Login = makeIcon(LoginSquare01Icon);
export const Logout = makeIcon(LogoutSquare01Icon);

// Ícones novos pro rebrand + reorganização de nav.
export const Rocket = makeIcon(Rocket02Icon);
export const Route = makeIcon(Route01Icon); // trajeto: linha do tempo de campanhas
export const AnalyticsUp = makeIcon(AnalyticsUpIcon);
export const TaskDaily = makeIcon(TaskDaily01Icon);
export const ShippingTruck = makeIcon(ShippingTruck01Icon);
export const Home3 = makeIcon(Home03Icon);
export const Storefront = makeIcon(Store01Icon);
export const Tag = makeIcon(Tag01Icon);
export const Trash = makeIcon(Delete02Icon);
export const TrendDown = makeIcon(ChartDecreaseIcon);
export const TrendUp = makeIcon(ChartIncreaseIcon);
export const Trophy = makeIcon(ChampionIcon);
export const Truck = makeIcon(TruckIcon);
export const User = makeIcon(UserIcon);
export const UserPlus = makeIcon(UserAddIcon);
export const Users = makeIcon(UserMultipleIcon);
export const UsersThree = makeIcon(UserGroup03Icon);
export const Warning = makeIcon(Alert02Icon);
export const WarningCircle = makeIcon(AlertCircleIcon);
export const Wrench = makeIcon(WrenchIcon);
export const X = makeIcon(Cancel01Icon);
export const XCircle = makeIcon(CancelCircleIcon);

// Ícones novos (sem equivalente phosphor) — usados na /operacoes pra substituir emojis.
export const ShoppingCart = makeIcon(ShoppingCart02Icon);
export const Warehouse = makeIcon(WarehouseIcon); // depósitos
export const FileExport = makeIcon(FileExportIcon); // exportar CSV
export const Drag = makeIcon(Drag01Icon); // splitter
export const MoreVertical = makeIcon(MoreVerticalIcon); // menus contextuais
export const Boat = makeIcon(BoatIcon); // menu Navios

// Precificação (custo fiscal por SKU).
export const Calculator = makeIcon(Calculator01Icon); // menu/aba Precificação
export const Percent = makeIcon(PercentCircleIcon); // parâmetros fiscais
